import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Observable } from 'rxjs';
import { map, skipWhile, switchMap, take, tap } from 'rxjs/operators';

import { MapperPipe } from 'tiime-expert-utils';
import {
  TiimeOptionModule,
  TiimeButtonModule,
  TiimeDatepickerModule,
  TiimeDialogModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeSelectModule
} from 'tiime-material';

import { AccountingConfigApiService } from '@api-services/accounting-config-api.service';
import { CustomAccountingPeriodDialogBase } from '@bases/custom-accounting-period-dialog/custom-accounting-period-dialog.base';
import { CustomAccountingPeriodLabel } from '@forms/custom-accounting-period-form';
import { DateHelper } from '@helpers/date.helper';
import { AccountingConfig } from '@models/accounting-config';
import { AccountingPeriod } from '@models/accounting-period';
import * as AccountingConfigActions from '@modules/company/company-core/store/accounting-config/accounting-config-actions';
import { accountingConfigSelector } from '@modules/company/company-core/store/accounting-config/accounting-config-selector';
import { openedAccountingPeriodSelector } from '@modules/company/company-core/store/accounting-periods/accounting-periods-selector';
import { EditCustomPeriodDialogData } from '@shared-dialogs/edit-custom-period-dialog/edit-custom-period-dialog-data';

@Component({
  standalone: true,
  selector: 'app-edit-custom-period-dialog',
  templateUrl: './../../../core/bases/custom-accounting-period-dialog/custom-accounting-period-dialog.base.html',
  styleUrls: [
    './../../../core/bases/custom-accounting-period-dialog/custom-accounting-period-dialog.base.scss',
    './edit-custom-period-dialog.component.scss'
  ],
  imports: [
    AsyncPipe,
    DatePipe,
    MapperPipe,
    TiimeOptionModule,
    ReactiveFormsModule,
    TiimeButtonModule,
    TiimeDatepickerModule,
    TiimeDialogModule,
    TiimeIconModule,
    TiimeInputContainerModule,
    TiimeSelectModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditCustomPeriodDialogComponent extends CustomAccountingPeriodDialogBase<number> {
  protected accountingPeriods: Signal<AccountingPeriod[]> = toSignal(this.store.select(openedAccountingPeriodSelector));
  protected needToRemoveOneDayToMaxDate: boolean = true;
  protected customAccountingPeriodLabel: CustomAccountingPeriodLabel = 'période';
  protected title: string = 'Période personnalisée';
  protected startDatePlaceholder: string = 'Date de début de la période personnalisée';
  protected endDatePlaceholder: string = 'Date de fin de la période personnalisée';
  protected instruction: string = `La période personnalisée vous permet de visualiser la balance, le grand livre, et les journaux à une date de fin de vision spécifique, autre que la date de fin de l’exercice comptable.<br /><br />Vous avez également la possibilité d’effectuer les éditions comptables sur une période personnalisée.`;

  private readonly accountingConfig$: Observable<AccountingConfig> = this.store.select(accountingConfigSelector);
  private accountingPeriodFromData: AccountingPeriod;

  constructor(
    private accountingConfigApiService: AccountingConfigApiService,
    protected dialogRef: MatDialogRef<EditCustomPeriodDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: EditCustomPeriodDialogData
  ) {
    super(dialogRef);
    this.accountingPeriodFromData = data.accountingPeriod;
  }

  save(): Observable<number> {
    return this.accountingConfig$.pipe(
      take(1),
      switchMap((accountingConfig: AccountingConfig) =>
        this.accountingConfigApiService.updatePartialAccountingConfig({
          id: accountingConfig.id,
          custom_accounting_period_end_date: DateHelper.format(this.form.endDate.value)
        })
      ),
      tap((updatedAccountingConfig: AccountingConfig) =>
        this.store.dispatch(
          AccountingConfigActions.updatePartial({
            accountingConfig: {
              customAccountingPeriodEndDate: updatedAccountingConfig.customAccountingPeriodEndDate
            }
          })
        )
      ),
      map((accountingConfig: AccountingConfig) => accountingConfig.id)
    );
  }

  initFormValue(): void {
    this.form.accountingPeriod.setValue(this.accountingPeriodFromData);

    this.accountingConfig$
      .pipe(
        skipWhile(accountingConfig => !accountingConfig),
        take(1),
        tap(accountingConfig => {
          if (!accountingConfig.customAccountingPeriodEndDate) {
            return;
          }

          setTimeout(() => this.form.endDate.setValue(accountingConfig.customAccountingPeriodEndDate));
        })
      )
      .subscribe();
  }
}
